// Login screen for the curator. Email + password → POST /auth/web-login.
// On success, AuthContext flips and the router navigates the user into the
// protected app. On failure (bad creds, banned, role-not-permitted, etc.)
// we surface the server's error message verbatim.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

export default function LoginScreen() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch {
      // AuthContext already populates `error` for us.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.outer}>
      <form onSubmit={handleSubmit} style={styles.card}>
        <h1 style={styles.title}>Curator</h1>
        <p style={styles.subtitle}>
          Editors and admins only. Sign in with your Global Village credentials.
        </p>

        {error ? <div style={styles.error}>{error}</div> : null}

        <label style={styles.label}>Email</label>
        <input
          style={styles.input}
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          required
          autoFocus
        />

        <label style={styles.label}>Password</label>
        <input
          style={styles.input}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
          required
        />

        <button
          type="submit"
          style={styles.button}
          disabled={submitting || !email.trim() || !password}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  outer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  card: {
    width: "100%",
    maxWidth: 360,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    padding: "32px",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: 24,
    fontWeight: 700,
  },
  subtitle: {
    margin: "0 0 24px 0",
    color: "var(--text-secondary)",
    fontSize: 14,
    lineHeight: 1.5,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-secondary)",
    marginBottom: 6,
    marginTop: 12,
  },
  input: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-primary)",
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
  },
  button: {
    marginTop: 24,
    background: "var(--accent)",
    color: "#0e0e0f",
    border: "none",
    borderRadius: "var(--radius-sm)",
    padding: "12px",
    fontWeight: 600,
    fontSize: 14,
  },
  error: {
    background: "rgba(239, 68, 68, 0.12)",
    border: "1px solid rgba(239, 68, 68, 0.4)",
    color: "#fca5a5",
    padding: "10px 12px",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    marginBottom: 16,
  },
};
